import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { api, ApiError, type Item } from "../api";
import { Thread, type Attachment, type Comment } from "../components/Thread";
import { ErrorNote, formatDate, KindTag, Loading, Moment, StatusChip, timeAgo, useApi } from "../components/ui";
import { useMe } from "../App";

export function CustomerTickets() {
  const { me } = useMe();
  const [filter, setFilter] = useState<"active" | "any">("active");
  const { data, error, loading } = useApi<{ items: Item[]; total: number }>(`/items?status=${filter}&limit=100`);
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{me.orgName}</p>
          <h1>Tickets</h1>
        </div>
        <Link href="/tickets/new" className="btn btn-primary" data-tour="new-ticket-button">
          New ticket
        </Link>
      </div>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={filter === "active"} className={filter === "active" ? "active" : ""} onClick={() => setFilter("active")}>
          Open
        </button>
        <button role="tab" aria-selected={filter === "any"} className={filter === "any" ? "active" : ""} onClick={() => setFilter("any")}>
          All
        </button>
      </div>
      <ErrorNote message={error} />
      {loading && !data ? (
        <Loading />
      ) : data && data.items.length === 0 ? (
        <div data-tour="tickets-list">
        <Moment title="No tickets, all clear">
          <p className="muted">Need something? Open a ticket and we'll take it from there.</p>
          <Link href="/tickets/new" className="btn btn-primary">
            New ticket
          </Link>
        </Moment>
        </div>
      ) : (
        <div className="card table-wrap" data-tour="tickets-list">
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Title</th>
                <th>Status</th>
                <th>From</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((t) => (
                <tr key={t.id}>
                  <td className="mono small">{t.ref}</td>
                  <td className="title-cell">
                    <Link href={`/tickets/${t.number}`}>{t.title}</Link>
                    <div>
                      <KindTag kind={t.kind} />
                      {t.commentCount ? <span className="small muted"> · {t.commentCount} repl{t.commentCount === 1 ? "y" : "ies"}</span> : null}
                    </div>
                  </td>
                  <td>
                    <StatusChip status={t.status} customer />
                  </td>
                  <td className="small">{t.reporterName}</td>
                  <td className="small muted">{timeAgo(t.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CATEGORIES = [
  ["bug", "Something's broken"],
  ["question", "Question / how do I…"],
  ["feature", "Feature request"],
  ["access", "Access or account"],
  ["billing", "Billing"],
  ["other", "Something else"],
] as const;

export function NewTicket() {
  const [, navigate] = useLocation();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("bug");
  const [files, setFiles] = useState<FileList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ ref: string; number: number } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ ref: string; number: number }>("/items", { method: "POST", json: { title, body, category } });
      for (const file of Array.from(files ?? [])) {
        const form = new FormData();
        form.append("file", file);
        await api(`/items/${res.number}/attachments`, { method: "POST", body: form }).catch(() => null);
      }
      setDone(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="page-narrow">
        <Moment mood="celebrate" title="Ticket logged">
          <p>
            You're cleared for takeoff: <strong className="mono">{done.ref}</strong>. We'll reply here and by email.
          </p>
          <div className="row" style={{ justifyContent: "center" }}>
            <button className="btn btn-primary" onClick={() => navigate(`/tickets/${done.number}`)}>
              View ticket
            </button>
            <Link href="/tickets" className="btn">
              All tickets
            </Link>
          </div>
        </Moment>
      </div>
    );
  }

  return (
    <div className="page-narrow">
      <p className="eyebrow">
        <Link href="/tickets">Tickets</Link> /
      </p>
      <h1 style={{ marginBottom: 20 }}>New ticket</h1>
      <form className="card stack" onSubmit={submit} data-tour="ticket-form">
        <div className="field">
          <label htmlFor="cat">What kind of request?</label>
          <select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
            {CATEGORIES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="title">Short summary</label>
          <input id="title" type="text" required minLength={3} maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="body">Details</label>
          <textarea id="body" value={body} onChange={(e) => setBody(e.target.value)} maxLength={20000} style={{ minHeight: 180 }} />
          <span className="hint">What happened, what you expected, and steps to reproduce if it's a bug.</span>
        </div>
        <div className="field">
          <label htmlFor="files">Screenshots or files (optional, 10 MB each)</label>
          <input id="files" type="file" multiple onChange={(e) => setFiles(e.target.files)} />
        </div>
        {error && <p className="error-text" role="alert">{error}</p>}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <Link href="/tickets" className="btn">
            Cancel
          </Link>
          <button className="btn btn-primary" disabled={busy} data-tour="ticket-submit">
            {busy ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      </form>
    </div>
  );
}

interface Detail {
  item: Item;
  comments: Comment[];
  attachments: Attachment[];
}

export function CustomerTicket({ number }: { number: string }) {
  const { data, error, loading, reload } = useApi<Detail>(`/items/${number}`);
  const [busy, setBusy] = useState(false);
  if (loading && !data) return <div className="page-narrow"><Loading /></div>;
  if (error || !data) {
    return (
      <div className="page-narrow">
        <Moment mood="thinking" title="We couldn't find that ticket">
          <p className="muted">{error}</p>
          <Link href="/tickets" className="btn">Back to tickets</Link>
        </Moment>
      </div>
    );
  }
  const t = data.item;
  const setStatus = async (status: "open" | "closed") => {
    setBusy(true);
    await api(`/items/${t.number}`, { method: "PATCH", json: { status } }).catch(() => null);
    setBusy(false);
    reload();
  };
  return (
    <div className="page-narrow">
      <p className="eyebrow">
        <Link href="/tickets">Tickets</Link> / {t.ref}
      </p>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", margin: "4px 0 16px" }}>
        <h1 style={{ flex: 1, minWidth: 240 }}>{t.title}</h1>
        <StatusChip status={t.status} customer />
      </div>
      {t.status === "resolved" && (
        <div className="card row" style={{ marginBottom: 16 }}>
          <span style={{ flex: 1 }}>We've marked this resolved. All good?</span>
          <button className="btn btn-small" disabled={busy} onClick={() => setStatus("closed")}>Yes, close it</button>
          <button className="btn btn-small" disabled={busy} onClick={() => setStatus("open")}>No, reopen</button>
        </div>
      )}
      <div className="card stack" style={{ marginBottom: 16 }}>
        <p className="small muted" style={{ margin: 0 }}>
          <KindTag kind={t.kind} /> · Opened by {t.reporterName} on {formatDate(t.createdAt)}
          {t.appName ? ` · ${t.appName}` : ""}
        </p>
        {t.body ? <div className="prewrap">{t.body}</div> : <p className="muted">No details provided.</p>}
      </div>
      <Thread itemKey={t.number} comments={data.comments} attachments={data.attachments} staff={false} onChange={reload} />
      {["open", "in_progress", "waiting"].includes(t.status) && t.kind === "ticket" && (
        <p style={{ marginTop: 16 }}>
          <button className="btn-link" disabled={busy} onClick={() => setStatus("closed")}>
            Close this ticket
          </button>
        </p>
      )}
      {t.status === "closed" && t.kind === "ticket" && (
        <p style={{ marginTop: 16 }}>
          <button className="btn-link" disabled={busy} onClick={() => setStatus("open")}>
            Reopen this ticket
          </button>
        </p>
      )}
    </div>
  );
}
